import React, { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { toMultiDropdownOptions } from 'shared/utils/formUtils';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import {
  MultiDropdown,
  useMultiDropdown,
} from '@grnhse/seedling/lib/birch/components/forms/dropdown';
import { TertiaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import { withLocalePrefix } from 'shared/utils/translation';
import PopoverButton from 'talent_matching/components/filters/popover_button';
import { useRediscoveryTabContext } from 'talent_matching/contexts/rediscovery_tab_context';
import DividerLabel from 'talent_search/components/divider_label';
import styled from 'styled-components';

const t = withLocalePrefix('talent_matching.rediscovery.filter_row');

const CenteredDivider = styled.div`
  display: flex;
  justify-content: center;
`;

const JobDetailsFiltersGroup = () => {
  const { apiClient } = useRediscoveryTabContext();
  const queryClient = useQueryClient();
  const { watch, setValue } = useFormContext();
  const [jobs, departments, offices] = watch([
    'jobs',
    'departments',
    'offices',
  ]);

  const fetchJobItems = useCallback(
    async (term: string) => {
      const queryKey = ['jobs', term];
      const cachedData: Item[] | null | undefined =
        queryClient.getQueryData(queryKey);
      if (cachedData) {
        return { items: cachedData };
      }
      const items = await queryClient.fetchQuery({
        queryKey,
        queryFn: async () => {
          const fetchedItems = await apiClient.fetchJobs(term);
          return toMultiDropdownOptions(fetchedItems);
        },
      });
      return { items };
    },
    [apiClient, queryClient]
  );

  const onSelectedJobsChange = (e: { selectedItems: Array<Item> }) => {
    setValue('jobs', e.selectedItems, { shouldDirty: true });
  };

  const jobsDropdownProps = useMultiDropdown({
    fetchItems: fetchJobItems,
    onSelectedItemsChange: onSelectedJobsChange,
    selectedItems: jobs,
    preventLoadOnMount: true,
  });

  const fetchDepartmentItems = useCallback(
    async (term: string) => {
      const queryKey = ['departments', term];
      const cachedData: Item[] | null | undefined =
        queryClient.getQueryData(queryKey);
      if (cachedData) {
        return { items: cachedData };
      }
      const items = await queryClient.fetchQuery({
        queryKey,
        queryFn: async () => {
          const fetchedItems = await apiClient.fetchDepartments(term);
          return toMultiDropdownOptions(fetchedItems);
        },
      });
      return { items };
    },
    [apiClient, queryClient]
  );

  const onSelectedDepartmentsChange = (e: { selectedItems: Array<Item> }) => {
    setValue('departments', e.selectedItems, { shouldDirty: true });
  };

  const departmentsDropdownProps = useMultiDropdown({
    fetchItems: fetchDepartmentItems,
    onSelectedItemsChange: onSelectedDepartmentsChange,
    selectedItems: departments,
    preventLoadOnMount: true,
  });

  const fetchOfficeItems = useCallback(
    async (term: string) => {
      const queryKey = ['offices', term];
      const cachedData: Item[] | null | undefined =
        queryClient.getQueryData(queryKey);
      if (cachedData) {
        return { items: cachedData };
      }
      const items = await queryClient.fetchQuery({
        queryKey,
        queryFn: async () => {
          const fetchedItems = await apiClient.fetchOffices(term);
          return toMultiDropdownOptions(fetchedItems);
        },
      });
      return { items };
    },
    [apiClient, queryClient]
  );

  const onSelectedOfficesChange = (e: { selectedItems: Array<Item> }) => {
    setValue('offices', e.selectedItems, { shouldDirty: true });
  };

  const officesDropdownProps = useMultiDropdown({
    fetchItems: fetchOfficeItems,
    onSelectedItemsChange: onSelectedOfficesChange,
    selectedItems: offices,
    preventLoadOnMount: true,
  });

  const hasJobs = Array.isArray(jobs) && jobs.length > 0;
  const hasDepartments = Array.isArray(departments) && departments.length > 0;
  const hasOffices = Array.isArray(offices) && offices.length > 0;
  const appliedCount = [hasJobs, hasDepartments, hasOffices].filter(
    Boolean
  ).length;

  const clear = () => {
    setValue('jobs', [], { shouldDirty: true });
    setValue('departments', [], { shouldDirty: true });
    setValue('offices', [], { shouldDirty: true });
  };

  const buttonLabel = `${t('job_details_filters')}${
    appliedCount ? ` (${appliedCount})` : ''
  }`;

  return (
    <PopoverButton
      buttonLabel={buttonLabel}
      title={t('job_details_filters')}
      width="520px"
    >
      <Stack spacing={1}>
        <MultiDropdown dropdownProps={departmentsDropdownProps}>
          <MultiDropdown.Field
            data-provides="talent-rediscovery-tm-departments-dropdown"
            label={t('labels.departments')}
            condensedUnitName={t('labels.departments')}
          />
          <MultiDropdown.Flyout />
        </MultiDropdown>

        <CenteredDivider>
          <DividerLabel>{t('or')}</DividerLabel>
        </CenteredDivider>

        <MultiDropdown dropdownProps={jobsDropdownProps}>
          <MultiDropdown.Field
            data-provides="talent-rediscovery-tm-jobs-dropdown"
            label={t('labels.jobs')}
            condensedUnitName={t('labels.jobs')}
            name="jobs"
          />
          <MultiDropdown.Flyout />
        </MultiDropdown>

        <CenteredDivider>
          <DividerLabel>{t('and')}</DividerLabel>
        </CenteredDivider>

        <MultiDropdown dropdownProps={officesDropdownProps}>
          <MultiDropdown.Field
            data-provides="talent-rediscovery-tm-offices-dropdown"
            label={t('labels.offices')}
          />
          <MultiDropdown.Flyout />
        </MultiDropdown>

        <Stack sx={{ alignSelf: 'flex-start' }}>
          <TertiaryButton onClick={clear} size="lg">
            {t('clear')}
          </TertiaryButton>
        </Stack>
      </Stack>
    </PopoverButton>
  );
};

export default JobDetailsFiltersGroup;
